import type { ReactNode } from 'react'
import { useEffect, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { ApiError } from '@/api/client'
import { LoadingScreen } from '@/components/LoadingScreen'
import { useCreateCompanyPouleCheckout, usePouleBilling, type BillingProduct } from '@/features/poules/billingApi'
import { usePouleInfo } from '@/features/poules/api'
import { useTranslation } from '@/i18n'

export function PouleBillingPage() {
  const { t, locale } = useTranslation()
  const { pouleId } = useParams()
  const [searchParams] = useSearchParams()
  const numericPouleId = Number(pouleId)
  const validPouleId = Number.isInteger(numericPouleId) && numericPouleId > 0 ? numericPouleId : null
  const paymentStatus = searchParams.get('billing')
  const [selectedProductKey, setSelectedProductKey] = useState<string | null>(null)
  const billing = usePouleBilling(validPouleId, paymentStatus === 'success')
  const checkout = useCreateCompanyPouleCheckout(validPouleId)
  const poule = usePouleInfo(validPouleId)

  useEffect(() => {
    if (paymentStatus !== 'success' || billing.data?.license?.status === 'active') return
    void billing.refetch()
  }, [billing.data?.license?.status, billing.refetch, paymentStatus])

  if (!validPouleId) {
    return <BillingState title={t('Ongeldige poule')} body={t('De URL bevat geen geldig poule-id.')} retryLabel={t('Opnieuw proberen')} />
  }

  if (billing.isLoading || poule.isLoading) {
    return <LoadingScreen compact />
  }

  if (billing.isError || !billing.data || poule.isError || !poule.data) {
    const isForbidden = billing.error instanceof ApiError && billing.error.status === 403
    return (
      <BillingState
        title={isForbidden ? t('Geen beheerrechten') : t('Licentiegegevens konden niet worden geladen.')}
        body={isForbidden ? t('Je hebt geen beheerrechten voor deze poule.') : t('Probeer het later opnieuw.')}
        retry={!isForbidden ? () => void billing.refetch() : undefined}
        retryLabel={t('Opnieuw proberen')}
      />
    )
  }

  const state = billing.data

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link to={'/poules/' + validPouleId} className="text-sm font-semibold text-surface-400 transition hover:text-white">
            ← {t('Terug naar poule')}
          </Link>
          <p className="mt-5 text-[10px] font-bold uppercase tracking-[0.2em] text-accent-200">{t('Beheer')}</p>
          <h1 className="mt-1 text-2xl font-black tracking-tight text-white sm:text-3xl">{t('Bedrijfspoulelicentie')}</h1>
          <p className="mt-2 text-sm text-surface-400">{poule.data.poule.name}</p>
          {paymentStatus === 'success' && state.license?.status === 'active' ? (
            <p className="mt-3 text-sm font-semibold text-emerald-300">
              {t('Betaling geslaagd. {product} is nu actief.', {
                product: productName(state.license.product_key, state.products, t),
              })}
            </p>
          ) : null}
        </div>
      </header>

      {paymentStatus === 'success' && state.license?.status !== 'active' ? (
        <Notice tone="success">{t('Betaling ontvangen. De licentie wordt geactiveerd…')}</Notice>
      ) : null}
      {paymentStatus === 'cancelled' ? <Notice tone="muted">{t('Betaling geannuleerd.')}</Notice> : null}

      {state.license?.status !== 'active' ? (
        <Notice tone="muted">{t('Deze bedrijfspoule is aangemaakt, maar nog niet actief. Kies een licentie om deelnemers uit te nodigen en de poule te gebruiken.')}</Notice>
      ) : null}

      <section className="space-y-4">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-surface-500">{t('Pakketten')}</p>
          <h2 className="mt-1 text-2xl font-black tracking-tight text-white">{t('Kies een bedrijfspakket')}</h2>
          <p className="mt-2 text-sm leading-6 text-surface-400">{t('Prijzen exclusief btw. Het toepasselijke belastingbedrag wordt berekend bij het afrekenen.')}</p>
        </div>
        <div className="grid gap-4 lg:grid-cols-3">
          {state.products.map((product) => (
            <ProductCard
              key={product.key}
              product={product}
              locale={locale}
              t={t}
              active={state.license?.product_key === product.key && state.license.status === 'active'}
              isAvailable={product.available !== false}
              isPending={checkout.isPending && selectedProductKey === product.key}
              onChoose={() => {
                if (checkout.isPending) return
                setSelectedProductKey(product.key)
                checkout.mutate(
                  { productKey: product.key, customerType: 'business' },
                  {
                    onSuccess: (result) => window.location.assign(result.checkout_url),
                    onError: () => setSelectedProductKey(null),
                  },
                )
              }}
            />
          ))}
        </div>
        {checkout.error ? <p className="text-sm text-red-300">{t('Betaling starten is mislukt.')}</p> : null}
        <p className="text-xs text-surface-500">{t('De btw wordt berekend tijdens het afrekenen.')}</p>
      </section>
    </div>
  )
}

function ProductCard({
  product,
  locale,
  t,
  active,
  isAvailable,
  isPending,
  onChoose,
}: {
  product: BillingProduct
  locale: string
  t: (key: string, values?: Record<string, string | number>) => string
  active: boolean
  isAvailable: boolean
  isPending: boolean
  onChoose: () => void
}) {
  const amount = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: product.currency.toUpperCase(),
  }).format((product.amount_due ?? product.amount) / 100)
  const packageTotal = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: product.currency.toUpperCase(),
  }).format(product.amount / 100)

  const unavailable = !active && !isAvailable

  return (
    <article className={active ? 'flex h-full flex-col rounded-[1.5rem] border border-emerald-400/30 bg-emerald-400/[0.08] p-5 sm:p-6' : 'flex h-full flex-col rounded-[1.5rem] border border-white/[0.07] bg-[#0d1113] p-5 sm:p-6'}>
      <p className="text-lg font-black text-white">{productName(product.key, [product], t)}</p>
      <p className="mt-2 text-xl font-bold tracking-tight text-surface-200">{product.is_upgrade ? '+ ' : ''}{amount}</p>
      <p className="mt-1 text-xs text-surface-500">{product.is_upgrade ? t('Bij te betalen voor dit seizoen') : t('Per seizoen')}</p>
      {product.is_upgrade ? <p className="mt-1 text-[11px] text-surface-500">{t('Totaal pakket: {amount}', { amount: packageTotal })}</p> : null}
      <p className="mt-5 min-h-10 text-sm leading-6 text-surface-300">
        {product.participant_limit === null || product.participant_limit === undefined
          ? t('Onbeperkt deelnemers')
          : t('Tot en met {count} deelnemers', { count: product.participant_limit })}
      </p>
      {active ? (
        <div className="mt-auto flex min-h-11 items-center justify-center border-t border-emerald-400/15 pt-3 text-sm font-semibold text-emerald-200" role="status">
          <span className="mr-2 h-1.5 w-1.5 rounded-full bg-emerald-300" aria-hidden="true" />
          {t('Huidig pakket')}
        </div>
      ) : (
        <button
          type="button"
          disabled={unavailable || isPending}
          onClick={onChoose}
          className="mt-auto inline-flex min-h-11 w-full items-center justify-center rounded-xl bg-accent-600 px-4 py-3 text-sm font-bold text-white transition hover:bg-accent-500 disabled:cursor-wait disabled:opacity-60"
        >
          {unavailable ? t('Niet beschikbaar') : isPending ? t('Betaling openen…') : product.is_upgrade ? t('Upgraden') : t('Pakket kiezen')}
        </button>
      )}
    </article>
  )
}

function productName(key: string, products: BillingProduct[], t: (key: string) => string) {
  if (key === 'company') return t('Bedrijfspoule')
  if (key === 'company_pro') return t('Bedrijfspoule Pro')
  if (key === 'company_unlimited') return t('Bedrijfspoule Ultimate')

  const product = products.find((item) => item.key === key)
  if (product?.label) return t(product.label)
  return t('Bedrijfspoule')
}

function Notice({ children, tone }: { children: ReactNode; tone: 'success' | 'muted' }) {
  return (
    <div className={tone === 'success' ? 'rounded-2xl border border-emerald-400/20 bg-emerald-400/[0.08] p-4 text-sm text-emerald-100' : 'rounded-2xl border border-white/[0.07] bg-[#0d1113] p-4 text-sm text-surface-300'}>
      {children}
    </div>
  )
}

function BillingState({ title, body, retry, retryLabel }: { title: string; body: string; retry?: (() => void) | undefined; retryLabel: string }) {
  return (
    <section className="rounded-[1.5rem] border border-white/[0.07] bg-[#0d1113] p-6">
      <h1 className="text-xl font-black text-white">{title}</h1>
      <p className="mt-2 text-sm text-surface-400">{body}</p>
      {retry ? (
        <button type="button" onClick={retry} className="mt-5 rounded-xl bg-accent-600 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-accent-500">
          {retryLabel}
        </button>
      ) : null}
    </section>
  )
}
